import { t } from "@lingui/core/macro";
import { Button, Tooltip } from "@heroui/react";
import dayjs from "dayjs";
import { RiPauseCircleLine } from "react-icons/ri";
import { useShallow } from "zustand/react/shallow";
import { resolveTimeOffTypeName } from "@/pages/Dashboard/Schedule/Components/TimeOffTypes/timeOffTypeDisplayName";
import {
  type CalendarTimeOff,
  useCalendarStore,
} from "../../store/useCalendarStore";
import type { MonthDayAbsence } from "./monthTimeOffs";

/** A client-made type may carry no colour of its own. */
const UNTYPED_COLOR = "#969BA9";

function timeOffLabel(timeOff: CalendarTimeOff): string {
  return (
    resolveTimeOffTypeName({
      name: timeOff.typeName,
      systemKey: timeOff.typeSystemKey,
    }) ||
    timeOff.title ||
    t`Pavza`
  );
}

function useOpenDay(date: string) {
  const setSelectedDateAndView = useCalendarStore(
    (state) => state.dateActions.setSelectedDateAndView,
  );
  return () => setSelectedDateAndView(dayjs(date), "day");
}

export function MonthTimeOffBlock({
  timeOff,
  start,
  date,
}: {
  timeOff: CalendarTimeOff;
  start: string;
  date: string;
}) {
  const { users, resources } = useCalendarStore(
    useShallow((state) => ({
      users: state.users,
      resources: state.resources,
    })),
  );
  const openDay = useOpenDay(date);

  const owner =
    timeOff.source === "staff"
      ? users.find((user) => user.userId === timeOff.userId)?.name
      : resources.find((resource) => resource.id === timeOff.resourceId)?.label;
  const color = timeOff.typeColor ?? UNTYPED_COLOR;
  const label = timeOffLabel(timeOff);

  return (
    <Button
      size="sm"
      className="h-6 w-full justify-between gap-1 rounded-sm border-2 border-l-4 bg-white px-1 dark:bg-black"
      style={{ borderColor: color }}
      onPress={openDay}
    >
      <span
        className="flex min-w-0 items-center gap-1 text-xs"
        style={{ color }}
      >
        <span className="shrink-0">
          {timeOff.typeEmoji ?? <RiPauseCircleLine />}
        </span>
        <span className="truncate">
          {owner ? `${owner} · ${label}` : label}
        </span>
      </span>
      <span className="shrink-0 text-foreground text-xs">
        {dayjs(start).format("HH:mm")}
      </span>
    </Button>
  );
}

export function MonthAbsenceBadge({
  absences,
  date,
}: {
  absences: MonthDayAbsence[];
  date: string;
}) {
  const users = useCalendarStore((state) => state.users);
  const openDay = useOpenDay(date);

  const people = absences.map(({ userId, timeOffs }) => {
    const user = users.find((u) => u.userId === userId);
    return {
      userId,
      shortName: user?.name ?? "",
      fullName: [user?.name, user?.lastName].filter(Boolean).join(" "),
      emoji: timeOffs[0].typeEmoji,
      color: timeOffs[0].typeColor ?? UNTYPED_COLOR,
      labels: [...new Set(timeOffs.map(timeOffLabel))].join(", "),
    };
  });

  return (
    <Tooltip
      showArrow
      content={
        <div className="flex flex-col gap-1 py-1 text-xs">
          {people.map((person) => (
            <p key={person.userId}>
              {person.emoji ? `${person.emoji} ` : ""}
              <span className="font-semibold">{person.fullName}</span>
              {` · ${person.labels}`}
            </p>
          ))}
        </div>
      }
    >
      <Button
        size="sm"
        variant="bordered"
        onPress={openDay}
        className="h-5 min-w-0 gap-1 rounded-full border-1 bg-white px-1.5 text-xs dark:bg-black"
        style={{ borderColor: people[0].color, color: people[0].color }}
      >
        <span className="shrink-0">
          {people[0].emoji ?? <RiPauseCircleLine />}
        </span>
        <span className="truncate">
          {people.map((person) => person.shortName).join(", ")}
        </span>
      </Button>
    </Tooltip>
  );
}
